import { Datalayer, FieldViewModel, ICropping, IDataLayerType, IGeometry, IOmniaShell, isObject, LayerTypeId, LayerViewModel } from "../../omnia"
import { IFieldsService } from "../../types"
import { showPopup } from "../simplify/config"
import { ILayerCreateContext } from "../types"
import { getAllLayers, getClippingFunction } from "../utils"
import { createCroppingLayer, createHoldingCroppingLayer, includeCroppingLayer } from "./cropping"
import { getLayerTypes } from "./layers"
import { SourceLayerType, SourceLayerViewModel } from "./types"

import IMapTopologyService = omnia.map.IMapTopologyService
import LayerValueType = omnia.models.LayerValueType

export class CopyLayerCtrl {

    static create(): ng.IDirective {

        return omnia.defineDirective(CopyLayerCtrl, "app/datalayers/edit/copy/copy.html", {
            targetType: "=",
            sourceLayers: "=",
            field: "=",
            context: "="
        })

    }

    private _context: ILayerCreateContext
    private _targetType: IDataLayerType
    private _sourceLayers: LayerViewModel[]
    private _field: FieldViewModel

    private _selectedLayer: SourceLayerViewModel

    public availableLayers: SourceLayerViewModel[]

    private _displayedLayers: SourceLayerViewModel[] = []

    public layerTypes: SourceLayerType[]
    public selectedType: SourceLayerType

    public dynamicScale: boolean

    static $inject = ["OmniaShell", "OmniaFieldsService", "OmniaMapTopologyService"]

    constructor(private shell: IOmniaShell, private fieldSvc: IFieldsService, private topo: IMapTopologyService) {
    }


    get context() {
        return this._context
    }

    set context(value) {
        this._context = value
        this.setup()
    }

    get targetLayer() {
        return this.context && this.context.layer
    }

    get targetType() {
        return this._targetType
    }

    set targetType(value: IDataLayerType) {
        this._targetType = value
        this.setup()
    }

    get sourceLayers(): LayerViewModel[] {
        return this._sourceLayers
    }

    set sourceLayers(value: LayerViewModel[]) {
        this._sourceLayers = value
        this.setup()
    }

    get field(): FieldViewModel {
        return this._field
    }

    set field(value: FieldViewModel) {
        this._field = value
        this.setup()
    }

    get selectedLayer(): SourceLayerViewModel {
        return this._selectedLayer
    }

    set selectedLayer(value: SourceLayerViewModel) {
        if (this._selectedLayer == value) return
        this._selectedLayer = value
        this.updateVisibleLayers()
    }

    updateVisibleLayers() {


        const s = this.getActualSelection()
        this._displayedLayers.forEach(l => l.visible = false)

        if (!s) {
            this._displayedLayers = []
        }
        else {
            this._displayedLayers = s.visible ? [] : [s]
            s.visible = true
        }

    }

    getActualSelection() {

        let l = this._selectedLayer

        if (l && this.dynamicScale && l.datalayer && l.getVariants) {
            const v = l.getVariants() || []
            l = v[0] || l
        }

        return l

    }

    onTypeSelected() {
        this.selectedLayer = this.selectedType
            ? this.selectedType.layers[0]
            : null
    }

    private setup() {

        let layers: SourceLayerViewModel[] = getAllLayers(this._sourceLayers, false)

        const type = (this.targetLayer ? this.targetLayer.type : null) || this.targetType

        const generic = !!(type && type.GenericFeatures)
        layers = layers.filter(l => (!!l.datalayer.Type.GenericFeatures) == generic)

        const pictures = !!(type && (type.TypeId == LayerTypeId.Picture))
        layers = layers.filter(l => (!!(l.datalayer.TypeId == LayerTypeId.Picture)) == pictures)

        if ((!pictures) && includeCroppingLayer(this._field)) {
            layers.push(createCroppingLayer(this._field))
        }

        if (generic && (!this._field)) {
            layers.push(createHoldingCroppingLayer(this.fieldSvc))
        }

        this.availableLayers = layers

        this.layerTypes = getLayerTypes(layers)

    }

    canSimplify() {

        const source = this.selectedLayer ? this.selectedLayer.datalayer : undefined
        const sourceType = source ? source.Type : undefined

        if (!sourceType) return false
        if (sourceType.ValueType != LayerValueType.Numeric) return false

        const targetType = this.targetType
        if (!targetType) return false

        switch (targetType.TypeId) {

            case LayerTypeId.SoilAnalysis:
            case LayerTypeId.PcnAnalysis:
            case LayerTypeId.Drawing:
            case LayerTypeId.Picture:
                return false

        }

        return true

    }

    simplify() {

        if (!(this.canCopy() && this.canSimplify())) {
            return
        }

        const sourceLayer = this.getActualSelection()
        if (!sourceLayer.zonesLoaded) return

        const dynamicScale = sourceLayer.datalayer?.Parent && this.dynamicScale

        const targetType = this.targetType

        const p = showPopup(sourceLayer.datalayer, sourceLayer.field, dynamicScale, targetType, this.shell)

        if (p) {

            p.then(r => {

                if (isObject(r)) {
                    this.copyFromLayer(<SourceLayerViewModel>r)
                }

            })

        }

    }

    canCopy() {

        if (this.targetLayer && this._selectedLayer && this._selectedLayer.zonesLoaded) {
            if (this._selectedLayer.datalayer) return true
            if (this.targetLayer.type) return true
        }

    }

    copy() {

        if (!this.canCopy()) return

        const s = this.getActualSelection()

        if (s.datalayer) {
            return this.copyFromLayer(s)
        }

        if (s.croppingList) {
            return this.copyFromCropping(s.croppingList)
        }

    }

    private copyFromLayer(source: SourceLayerViewModel) {

        if (!source.zonesLoaded) return

        if (!copyFromLayer(this.targetLayer, source, this.field, this.topo)) {
            return false
        }

        this.context.create()

        /** close(false) to avoid unselecting the source layer, which would also hide it */
        this.close(false)

        return true

    }

    private copyFromCropping(cropping: ICropping[]) {


        this.targetLayer.removeAllZones()
        this.targetLayer.copyZonesFromCropping(cropping)

        this.context.create()
        this.close()

        return true

    }

    close(unselect = true) {

        if (unselect) {
            this.selectedLayer = null
        }

    }

}

function copyFromLayer(target: Datalayer, source: SourceLayerViewModel, field: FieldViewModel, topo: IMapTopologyService) {

    let sameType = (target.type && source.checkTypeId(target.typeId))

    let clipping: (geom: IGeometry) => IGeometry

    if (field) {
        clipping = getClippingFunction(topo, field.getBoundary())
    }

    target.removeAllZones()
    target.copyZones(source.datalayer.Zones, sameType, clipping)

    return true

}
